import React, { useCallback, useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import debounce from 'lodash.debounce';
import { PERMISSIONS } from '@repo/shared/domain';
import { Project } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission } from '@repo/shared/helpers';
import { EventEmitter } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { Button } from '@repo/components';
import { DataGrid, DataGridColumn } from '@repo/components';
import { ChipList } from '@repo/components';
import { FilterDateRange } from '@repo/components';
import { GeneralTooltip as Tooltip } from '@repo/components';
import { ConditionalWrapper } from '@repo/components';
import { FilterSearch } from '@repo/components';
import { getPartnerCompanyProjects, getPartnerCompany, getPrincipalGroupLabelsSettings } from 'src/api/partner';
import { ProjectForm } from '@repo/forms';
import { Plus, User } from 'lucide-react';
import Paper from '@mui/material/Paper';
import { stringToSort } from '@repo/shared/helpers';
import { startOfDay, endOfDay } from 'date-fns';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/projects/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		from: (search.from as string) || '',
		until: (search.until as string) || '',
		page: search.page !== undefined ? Number(search.page) : 0,
		sort: (search.sort as string) || 'startDate:asc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Projects');
	const { search, from, until, page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [activeProject, setActiveProject] = useState<Project | undefined>();

	// get partner company
	const { data: partnerCompany } = useQuery({
		queryKey: ['partner-company', partnerId],
		queryFn: () => getPartnerCompany(partnerId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// get principal group labels settings
	const { data: principalGroupLabelsSettings } = useQuery({
		queryKey: ['principal-group-labels-settings'],
		queryFn: () => getPrincipalGroupLabelsSettings(),
		enabled: !userLoading && !!authenticatedUser,
	});

	const principalGroupLabels = useMemo(
		() => principalGroupLabelsSettings?.settings?.principalGroupLabels?.map((item) => item.value) || [],
		[principalGroupLabelsSettings],
	);

	const {
		data: projectsResponse,
		isLoading: projectsLoading,
		error: _getPartnerCompanyProjectsError,
	} = useQuery({
		queryKey: ['partner-company-projects', partnerId, { search, page, sort, from, until }],
		queryFn: () =>
			getPartnerCompanyProjects(
				partnerId,
				constructQuery({ search, page: page.toString(), sort, from, until, noPagination: 'false' }),
			),
		enabled: !userLoading && !!authenticatedUser,
	});

	const projects = projectsResponse?.data || [];

	const updateQueryParams = useCallback(
		(params: Record<string, string | undefined>) => {
			navigate({
				search: (prev) => ({ ...prev, ...params }),
			});
			return null;
		},
		[navigate],
	);

	if (!verifyPermission(PERMISSIONS.partnerCompanyProjectsView)) {
		navigate({ to: `/partner/${partnerId}` });
		return null;
	}

	// Loading = gating initial load
	if (userLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	const handleSort = (sortModel: Array<{ field: string; sort: string }>) => {
		if (sortModel.length > 0) {
			const { field, sort: sortDir } = sortModel[0];
			updateQueryParams({ sort: `${field}:${sortDir}` });
		}
	};

	const handlePageChange = (newPage: number) => {
		updateQueryParams({ page: newPage });
	};

	const handleRowClick = ({ row }: { row: Project }) => {
		navigate({ to: `/partner/${partnerId}/projects/${row.id}` });
		return null;
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleDateFilterChange = (dateFilter: { from: string; until: string }) => {
		updateQueryParams(dateFilter);
	};

	const handleCloseForm = () => {
		setIsFormOpen(false);
		setActiveProject(undefined);
	};

	const handleSubmit = async (projectData: Partial<Project> & { id?: string }) => {
		let isError;
		let id;

		if (projectData) {
			try {
				const response = await fetch(`/api/partner-portal/groups/${partnerId}/projects`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(projectData),
				});

				if (!response.ok) throw new Error('Failed to save project');

				const body = await response.json();
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Project saved',
				});
				id = body.id;
			} catch (error: unknown) {
				isError = true;
				const errorMessage =
					(error as { message?: string; body?: { message?: string } })?.message ||
					(error as { message?: string; body?: { message?: string } })?.body?.message ||
					'Failed to save Project. Did you enter all data correctly?';
				EventEmitter.emit('toast', {
					type: 'error',
					message: errorMessage,
				});
			}
		}

		if (id) {
			navigate({ to: `/partner/${partnerId}/projects/${id}` });
		} else {
			queryClient.invalidateQueries({ queryKey: ['partner-company-projects', partnerId] });
		}

		if (!isError) {
			setIsFormOpen(false);
			setActiveProject(undefined);
		}
	};

	const defaultProjectData = {
		principalGroups: [{ groupId: partnerId, label: 'Partner Company' }],
		principalGroupsPopulated: [partnerCompany],
		startDate: startOfDay(new Date()),
		endDate: endOfDay(new Date()),
	};

	const columns: DataGridColumn<Project>[] = [
		{
			field: 'name',
			headerName: 'Name',
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			valueFormatter: (params) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'endDate',
			headerName: 'End Date',
			valueFormatter: (params) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
		},
		{
			field: 'principalGroupsPopulated',
			headerName: 'Responsible Companies',
			renderCell: (c) => {
				if (!c.value || !Array.isArray(c.value)) {
					return null;
				}

				// Filter out duplicate groups by id
				const uniqueGroups = c.value.filter((group, index, self) => index === self.findIndex((g) => g.id === group.id));

				return <ChipList data={uniqueGroups} getLabel={(group) => group.name} />;
			},
		},
		{
			field: 'admins',
			headerName: 'Contacts',
			flex: 1,
			renderCell: (c) => (
				<ChipList
					data={c.value?.map((user: any) => ({ ...user, status: undefined }))}
					getLabel={(o) => (o.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email)}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
		{
			field: 'updatedAt',
			headerName: 'Updated',
			valueFormatter: (params) => params.value && new Date(params.value).toLocaleDateString('en-GB'),
		},
	];

	return (
		<div>
			<div className="p-4 pt-0">
				{/* Filter */}
				<div className="mt-2 flex flex-row gap-2">
					<div className="w-3/4">
						<FilterSearch
							label="Search Project"
							defaultValue={search || ''}
							onChange={debounce((searchInput: string) => {
								handleSearchChange(searchInput);
							}, 350)}
							sx={{ width: '100%' }}
						/>
					</div>
					<div className="w-1/4">
						<FilterDateRange
							label="Filter Date Range"
							value={
								from || until
									? {
											from,
											until,
										}
									: undefined
							}
							onChange={handleDateFilterChange}
							sx={{ width: '100%' }}
						/>
					</div>
					<ConditionalWrapper
						Wrapper={({ children }) => <Tooltip text="This action is not available for your role">{children}</Tooltip>}
						condition={!verifyPermission(PERMISSIONS.partnerCompanyProjectsCreate)}
					>
						<Button
							disabled={!verifyPermission(PERMISSIONS.partnerCompanyProjectsCreate)}
							variant="outline-bg-white"
							className="h-14"
							onClick={() => {
								setIsFormOpen(true);
							}}
						>
							<Plus />
							ADD PROJECT
						</Button>
					</ConditionalWrapper>
				</div>
				<div className="mb-4"></div> {/* Added for spacing */}
				<Paper variant="outlined">
					<DataGrid
						data={projects}
						isLoading={projectsLoading}
						rowCount={projectsResponse?.count || 0}
						page={projectsResponse?.page || 0}
						pageSize={projectsResponse?.pageSize || 10}
						columns={columns}
						sortModel={[stringToSort(sort || 'startDate:asc')]}
						onSortModelChange={handleSort}
						onReload={() => queryClient.invalidateQueries({ queryKey: ['partner-company-projects', partnerId] })}
						onPaginationModelChange={({ page }) => handlePageChange(page)}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						onRowClick={handleRowClick}
						pagination
					/>
				</Paper>
			</div>

			<ProjectForm
				isOpen={isFormOpen || !!activeProject}
				onClose={handleCloseForm}
				onSubmit={handleSubmit}
				data={activeProject || defaultProjectData}
				principalGroupLabels={principalGroupLabels}
				excludeZones={false}
				allowNewCompany={true}
			/>
		</div>
	);
}
